import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Alarm } from '@/api/alarms/types';
import { Brain, Send, Loader2, AlertCircle, AlertTriangle, Info, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Markdown } from '@/components/ui/markdown';
import { cn } from '@/lib/utils';

interface AIAnalysisProps {
  alarm: Alarm | null;
  alarms: Alarm[];
}

export function AIAnalysis({ alarm, alarms }: AIAnalysisProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [question, setQuestion] = useState('');
  const [conversation, setConversation] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [showSummary, setShowSummary] = useState(true);

  // Reference to the scrollable chat container
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Define functions with useCallback to prevent unnecessary re-renders
  const generateSummary = useCallback(() => {
    setIsLoading(true);

    // Simulate AI response delay
    setTimeout(() => {
      const summaryText = generateMockSummary(alarms);
      setSummary(summaryText);
      setConversation([{ role: 'assistant', content: summaryText }]);
      setIsLoading(false);
    }, 1000);
  }, [alarms]);

  const generateAnalysis = useCallback(() => {
    if (!alarm) return;

    setIsLoading(true);
    setAnalysis(null);

    // Simulate AI response delay
    setTimeout(() => {
      const analysisText = generateMockAnalysis(alarm);
      setAnalysis(analysisText);
      setConversation([{ role: 'assistant', content: analysisText }]);
      setIsLoading(false);
    }, 1000);
  }, [alarm]);

  const askQuestion = useCallback(() => {
    if (!question.trim()) return;

    const userQuestion = question.trim();
    // Add user message to conversation
    setConversation(prev => [...prev, { role: 'user', content: userQuestion }]);
    setQuestion('');
    setIsLoading(true);

    // Simulate AI response delay
    setTimeout(() => {
      const response = generateMockResponse(userQuestion, alarm, alarms, showSummary);
      // Only add the assistant's response, not the user message again
      setConversation(prev => [...prev, { role: 'assistant', content: response }]);
      setIsLoading(false);
    }, 1000);
  }, [question, alarm, alarms, showSummary]);

  // Generate summary of all alarms when the component mounts or alarms change
  useEffect(() => {
    if (alarms.length > 0 && !summary) {
      generateSummary();
    }
  }, [alarms, generateSummary, summary]);

  // Generate analysis when an alarm is selected
  useEffect(() => {
    if (alarm) {
      setShowSummary(false);
      generateAnalysis();
    } else {
      setShowSummary(true);
    }
  }, [alarm, generateAnalysis]);

  // Scroll to bottom when conversation changes
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [conversation]);

  // Suggested prompts based on context
  const getSuggestedPrompts = () => {
    if (showSummary) {
      return [
        "What are the most critical alarms right now?",
        "Do you see any patterns in these alarms?",
        "What actions should I take first?",
        "How many active alarms need attention?"
      ];
    } else if (alarm) {
      return [
        `Why did this ${alarm.type} alarm occur?`,
        `How can I fix this issue in ${alarm.deviceName}?`,
        `How serious is this ${alarm.severity} alarm?`,
        `Are there similar alarms to this one?`
      ];
    }
    return [];
  };

  const handlePromptClick = (prompt: string) => {
    setQuestion(prompt);
  };

  return (
    <div style={{
      height: '100%',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        padding: '8px',
        borderBottom: '1px solid var(--color-light-grey)',
        flexShrink: 0,
        backgroundColor: 'white',
        zIndex: 1
      }}>
        <div className="flex items-center">
          <Brain className="h-3.5 w-3.5 text-primary mr-1.5" />
          <h2 className="text-xs font-medium text-black">
            AltoGPT
          </h2>
        </div>
      </div>

      {/* Scrollable content area - using flex-grow instead of absolute positioning */}
      <div
        ref={chatContainerRef}
        style={{
          flexGrow: 1,
          overflowY: 'auto',
          padding: '12px',
          marginBottom: '8px'
        }}>
        {isLoading && (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="flex gap-1 mb-2">
              <div className="w-1.5 h-1.5 bg-primary rounded-full animate-breathe-dots"></div>
              <div className="w-1.5 h-1.5 bg-primary rounded-full animate-breathe-dots"></div>
              <div className="w-1.5 h-1.5 bg-primary rounded-full animate-breathe-dots"></div>
            </div>
            <p className="text-sm text-black">
              {showSummary ? "Analyzing all alarms..." : "Analyzing alarm data..."}
            </p>
          </div>
        )}

        {!isLoading && !summary && showSummary && (
          <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <div className="text-gray-400 mb-2">
              <Brain className="h-8 w-8 mx-auto stroke-[1.5px] text-primary opacity-50" />
            </div>
            <h3 className="text-sm font-medium text-black mb-1">No alarms to analyze</h3>
            <p className="text-sm text-gray-700">
              There are no alarms available for analysis.
            </p>
          </div>
        )}

        {!isLoading && !analysis && !showSummary && !alarm && (
          <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <div className="text-gray-400 mb-2">
              <AlertCircle className="h-8 w-8 mx-auto stroke-[1.5px]" />
            </div>
            <h3 className="text-sm font-medium text-black mb-1">No alarm selected</h3>
            <p className="text-sm text-gray-700">
              Select an alarm from the list to get AI analysis.
            </p>
          </div>
        )}

        {conversation.length > 0 && (
          <div className="space-y-4 pb-4">
            {conversation.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={cn(
                    "max-w-[90%] rounded-2xl py-2 px-3",
                    message.role === 'user'
                      ? "bg-[#E9F2FD] text-black flex items-center"
                      : "bg-white border border-light-grey text-black"
                  )}
                >
                  {message.role === 'assistant' && index === 0 && showSummary && (
                    <div className="mb-2 pb-2 border-b border-light-grey">
                      <div className="flex items-center">
                        <Brain className="h-3.5 w-3.5 text-primary mr-1.5" />
                        <h3 className="text-xs font-medium text-black">Alarm Summary</h3>
                      </div>
                    </div>
                  )}
                  {message.role === 'assistant' && index === 0 && !showSummary && (
                    <div className="mb-2 pb-2 border-b border-light-grey">
                      <div className="flex items-center">
                        <AlarmIcon alarm={alarm} />
                        <h3 className="text-xs font-medium text-black ml-1.5">
                          {alarm?.deviceName} - {alarm?.code}
                        </h3>
                      </div>
                    </div>
                  )}
                  {message.role === 'user' ? (
                    <span className="text-xs">{message.content}</span>
                  ) : (
                    <Markdown
                      content={message.content}
                      className="text-xs markdown-content text-black"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Input area - using flex-shrink instead of absolute positioning */}
      <div style={{
        flexShrink: 0,
        borderTop: '1px solid var(--color-light-grey)',
        padding: '8px',
        backgroundColor: 'white',
        zIndex: 1
      }}>
        {/* Suggested prompts */}
        {!isLoading && getSuggestedPrompts().length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {getSuggestedPrompts().map((prompt, index) => (
              <button
                key={index}
                onClick={() => handlePromptClick(prompt)}
                className="text-xs px-2 py-1 rounded-full bg-bg border border-light-grey text-black hover:bg-gray-100 transition-colors"
              >
                {prompt}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask a question about the alarms..."
            className="flex-1 rounded-l-md border border-light-grey py-1.5 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-bg text-black"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isLoading) {
                askQuestion();
              }
            }}
            disabled={isLoading}
          />
          <Button
            onClick={askQuestion}
            disabled={isLoading || !question.trim()}
            className="rounded-l-none bg-primary text-white hover:bg-primary/90 h-[30px] px-2"
          >
            {isLoading ? (
              <div className="flex gap-0.5">
                <div className="w-1 h-1 bg-current rounded-full animate-loading-dots"></div>
                <div className="w-1 h-1 bg-current rounded-full animate-loading-dots delay-150"></div>
                <div className="w-1 h-1 bg-current rounded-full animate-loading-dots delay-300"></div>
              </div>
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AlarmIcon({ alarm }: { alarm: Alarm | null }) {
  if (!alarm) return <AlertCircle className="h-4 w-4 text-primary" />;

  if (alarm.status === 'resolved') {
    return <CheckCircle className="h-4 w-4 text-success" />;
  }

  if (alarm.status === 'acknowledged') {
    return <Clock className="h-4 w-4 text-primary" />;
  }

  switch (alarm.severity) {
    case 'critical':
      return <AlertCircle className="h-4 w-4 text-danger" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-light-orange" />;
    case 'info':
      return <Info className="h-4 w-4 text-primary" />;
    default:
      return <AlertCircle className="h-4 w-4 text-primary" />;
  }
}

// Mock summary generator for all alarms
function generateMockSummary(alarms: Alarm[]): string {
  if (alarms.length === 0) {
    return "There are currently no alarms in the system.";
  }

  // Count alarms by status
  const activeCount = alarms.filter(a => a.status === 'active').length;
  const acknowledgedCount = alarms.filter(a => a.status === 'acknowledged').length;
  const resolvedCount = alarms.filter(a => a.status === 'resolved').length;

  // Count alarms by severity
  const criticalCount = alarms.filter(a => a.severity === 'critical').length;
  const warningCount = alarms.filter(a => a.severity === 'warning').length;
  const infoCount = alarms.filter(a => a.severity === 'info').length;

  // Count alarms by type
  const typeCount: Record<string, number> = {};
  alarms.forEach(alarm => {
    typeCount[alarm.type] = (typeCount[alarm.type] || 0) + 1;
  });

  // Find most affected devices
  const deviceCount: Record<string, number> = {};
  alarms.forEach(alarm => {
    deviceCount[alarm.deviceName] = (deviceCount[alarm.deviceName] || 0) + 1;
  });

  const sortedDevices = Object.entries(deviceCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  // Identify critical alarms that need immediate attention
  const criticalActiveAlarms = alarms.filter(a => a.severity === 'critical' && a.status === 'active');

  // Generate summary text
  let summary = `## Alarm System Overview\n\n`;

  summary += `### Current Status\n\n`;
  summary += `There are currently **${alarms.length} alarms** in the system:\n`;
  summary += `- **${activeCount} active** alarms requiring attention\n`;
  summary += `- **${acknowledgedCount} acknowledged** alarms being worked on\n`;
  summary += `- **${resolvedCount} resolved** alarms\n\n`;

  summary += `### Severity Breakdown\n\n`;
  summary += `- **${criticalCount} critical** alarms\n`;
  summary += `- **${warningCount} warning** alarms\n`;
  summary += `- **${infoCount} informational** alarms\n\n`;

  if (criticalActiveAlarms.length > 0) {
    summary += `### Priority Issues\n\n`;
    summary += `The following critical alarms require immediate attention:\n`;
    criticalActiveAlarms.slice(0, 3).forEach(alarm => {
      summary += `- **${alarm.deviceName}**: ${alarm.message} (${alarm.code})\n`;
    });
    if (criticalActiveAlarms.length > 3) {
      summary += `- And **${criticalActiveAlarms.length - 3} more critical alarms**\n`;
    }
    summary += '\n';
  }

  if (sortedDevices.length > 0) {
    summary += `### Most Affected Devices\n\n`;
    sortedDevices.forEach(([device, count]) => {
      summary += `- **${device}**: ${count} alarm${count > 1 ? 's' : ''}\n`;
    });
    summary += '\n';
  }

  summary += `### Recommendations\n\n`;
  if (criticalActiveAlarms.length > 0) {
    summary += `1. Address the ${criticalActiveAlarms.length} critical active alarms immediately\n`;
    summary += `2. Investigate potential patterns in ${sortedDevices[0]?.[0] || 'affected devices'}\n`;
    summary += `3. Consider scheduling maintenance for devices with multiple alarms\n`;
  } else if (activeCount > 0) {
    summary += `1. Review and acknowledge the ${activeCount} active alarms\n`;
    summary += `2. Monitor the system for any escalating issues\n`;
    summary += `3. Consider preventive maintenance for ${sortedDevices[0]?.[0] || 'affected devices'}\n`;
  } else {
    summary += `1. All alarms are currently under control\n`;
    summary += `2. Continue monitoring the system\n`;
    summary += `3. Review resolved alarms for potential patterns\n`;
  }

  return summary;
}

// Mock analysis generator
function generateMockAnalysis(alarm: Alarm): string {
  const analyses = {
    temperature: `## Temperature Alarm Analysis

This is a ${alarm.severity} temperature alarm on ${alarm.deviceName} (${alarm.deviceId}).

### What this means

The device has detected a temperature that exceeds its normal operating range. This could indicate an issue with the cooling system, airflow restriction, or environmental factors.

### Possible causes

1. Restricted airflow due to dirty filters or blocked vents
2. Refrigerant issues (low level or leakage)
3. Compressor malfunction
4. External heat sources affecting the area
5. Sensor malfunction providing false readings

### Recommended actions

1. Check and clean air filters
2. Verify that all vents are unobstructed
3. Inspect refrigerant levels and check for leaks
4. Examine the compressor for proper operation
5. Verify temperature sensor readings with a secondary device

### Impact

If not addressed, this issue could lead to reduced efficiency, increased energy consumption, and potential damage to the system components.`,

    communication: `## Communication Error Analysis

This is a ${alarm.severity} communication error on ${alarm.deviceName} (${alarm.deviceId}).

### What this means

The system is experiencing difficulties establishing or maintaining a connection with this device. This could be due to network issues, device hardware problems, or software configuration errors.

### Possible causes

1. Network connectivity issues
2. Device power supply problems
3. Controller hardware failure
4. Network configuration errors
5. Software or firmware bugs

### Recommended actions

1. Verify the device has power and is turned on
2. Check network cables and connections
3. Restart the device and network equipment
4. Verify network settings and configurations
5. Update device firmware if available

### Impact

Communication errors can prevent proper monitoring and control of the device, potentially leading to undetected issues or inability to make necessary adjustments.`,

    power: `## Power Fluctuation Analysis

This is a ${alarm.severity} power issue on ${alarm.deviceName} (${alarm.deviceId}).

### What this means

The system has detected abnormal power supply conditions. This could be due to unstable grid power, internal power supply issues, or electrical system problems.

### Possible causes

1. Unstable grid power supply
2. Internal power supply component failure
3. Loose electrical connections
4. Overloaded circuit
5. Voltage regulator issues

### Recommended actions

1. Check the main power supply and circuit breakers
2. Inspect electrical connections for any loose or damaged wires
3. Consider installing a power conditioner or UPS
4. Monitor power quality over time to identify patterns
5. Consult with an electrician if issues persist

### Impact

Power fluctuations can damage electronic components, cause unexpected system restarts, and reduce the lifespan of the equipment.`,

    sensor: `## Sensor Malfunction Analysis

This is a ${alarm.severity} sensor issue on ${alarm.deviceName} (${alarm.deviceId}).

### What this means

A sensor is providing inconsistent, out-of-range, or invalid readings. This affects the system's ability to accurately monitor and control the environment.

### Possible causes

1. Sensor hardware failure
2. Calibration drift
3. Environmental interference
4. Wiring or connection issues
5. Moisture or contamination affecting the sensor

### Recommended actions

1. Verify sensor readings with a calibrated external device
2. Check sensor connections and wiring
3. Clean the sensor if accessible
4. Recalibrate the sensor if possible
5. Replace the sensor if necessary

### Impact

Inaccurate sensor readings can lead to improper system operation, inefficient energy usage, and potential comfort or safety issues.`,

    system: `## System Error Analysis

This is a ${alarm.severity} system error on ${alarm.deviceName} (${alarm.deviceId}).

### What this means

The system has encountered an internal error that requires attention. This could be related to software, firmware, or critical hardware components.

### Possible causes

1. Software bug or corruption
2. Firmware issues
3. Memory or storage problems
4. Controller hardware failure
5. Configuration conflicts

### Recommended actions

1. Restart the system to clear temporary issues
2. Check system logs for specific error codes
3. Update firmware to the latest version
4. Verify system configuration settings
5. Contact technical support if the issue persists

### Impact

System errors can affect overall functionality and reliability, potentially leading to unexpected behavior or system downtime.`,
  };

  return analyses[alarm.type as keyof typeof analyses] ||
    `No specific analysis available for this alarm type. Please contact technical support for assistance with ${alarm.code} on ${alarm.deviceName}.`;
}

// Mock response generator
function generateMockResponse(question: string, alarm: Alarm | null, alarms: Alarm[], isShowingSummary: boolean): string {
  const lowercaseQuestion = question.toLowerCase();

  // Handle summary view questions
  if (isShowingSummary) {
    if (lowercaseQuestion.includes('most') && (lowercaseQuestion.includes('critical') || lowercaseQuestion.includes('important') || lowercaseQuestion.includes('urgent'))) {
      const criticalAlarms = alarms.filter(a => a.severity === 'critical' && a.status === 'active');
      if (criticalAlarms.length === 0) {
        return "There are currently no critical active alarms in the system. The highest priority would be any active warning alarms that need attention.";
      }

      let response = "The most critical alarms that need immediate attention are:\n\n";
      criticalAlarms.slice(0, 3).forEach((alarm, index) => {
        response += `${index + 1}. **${alarm.deviceName}**: ${alarm.message} (${alarm.code})\n`;
        response += `   - This is a ${alarm.severity} ${alarm.type} alarm that could impact system operation\n`;
      });

      if (criticalAlarms.length > 3) {
        response += `\nThere are ${criticalAlarms.length - 3} additional critical alarms that also require attention.`;
      }

      return response;
    }

    if (lowercaseQuestion.includes('recommend') || lowercaseQuestion.includes('suggest') || lowercaseQuestion.includes('should') || lowercaseQuestion.includes('next step')) {
      const activeCount = alarms.filter(a => a.status === 'active').length;
      const criticalCount = alarms.filter(a => a.severity === 'critical' && a.status === 'active').length;

      if (criticalCount > 0) {
        return `Based on the current alarm status, I recommend:\n\n1. **Immediately address the ${criticalCount} critical alarms** - these pose the highest risk to system operation\n2. Investigate potential patterns or common causes across multiple alarms\n3. After resolving critical alarms, work through the remaining ${activeCount - criticalCount} active alarms\n4. Consider scheduling preventive maintenance for devices with multiple alarms`;
      } else if (activeCount > 0) {
        return `Based on the current alarm status, I recommend:\n\n1. **Review and acknowledge all ${activeCount} active alarms**\n2. Prioritize alarms based on their potential impact on operations\n3. Check for any patterns in the types of alarms occurring\n4. Schedule regular maintenance for affected devices`;
      } else {
        return `All alarms appear to be under control (acknowledged or resolved). I recommend:\n\n1. Continue monitoring the system for new alarms\n2. Review the resolved alarms to identify any patterns that might indicate underlying issues\n3. Consider implementing preventive measures for common alarm types`;
      }
    }

    if (lowercaseQuestion.includes('pattern') || lowercaseQuestion.includes('trend') || lowercaseQuestion.includes('common')) {
      // Count alarms by type
      const typeCount: Record<string, number> = {};
      alarms.forEach(alarm => {
        typeCount[alarm.type] = (typeCount[alarm.type] || 0) + 1;
      });

      // Find most affected devices
      const deviceCount: Record<string, number> = {};
      alarms.forEach(alarm => {
        deviceCount[alarm.deviceName] = (deviceCount[alarm.deviceName] || 0) + 1;
      });

      const sortedTypes = Object.entries(typeCount)
        .sort((a, b) => b[1] - a[1]);

      const sortedDevices = Object.entries(deviceCount)
        .sort((a, b) => b[1] - a[1]);

      let response = "Based on the current alarms, I've identified the following patterns:\n\n";

      if (sortedTypes.length > 0) {
        response += "**Common Alarm Types:**\n";
        sortedTypes.slice(0, 3).forEach(([type, count]) => {
          response += `- ${count} ${type} alarms (${Math.round(count / alarms.length * 100)}% of total)\n`;
        });
        response += "\n";
      }

      if (sortedDevices.length > 0) {
        response += "**Most Affected Devices:**\n";
        sortedDevices.slice(0, 3).forEach(([device, count]) => {
          response += `- ${device}: ${count} alarm${count > 1 ? 's' : ''}\n`;
        });
        response += "\n";
      }

      if (sortedTypes.length > 0 || sortedDevices.length > 0) {
        response += "**Insight:** ";
        if (sortedTypes[0] && sortedDevices[0]) {
          response += `The high number of ${sortedTypes[0][0]} alarms, particularly on ${sortedDevices[0][0]}, suggests a potential systemic issue that should be investigated.`;
        } else if (sortedTypes[0]) {
          response += `The prevalence of ${sortedTypes[0][0]} alarms across multiple devices suggests a common environmental or operational factor may be contributing.`;
        } else if (sortedDevices[0]) {
          response += `The concentration of alarms on ${sortedDevices[0][0]} indicates this device may require maintenance or closer monitoring.`;
        }
      } else {
        response += "There are no clear patterns in the current alarms. They appear to be isolated incidents.";
      }

      return response;
    }

    return `I can help answer questions about the alarm summary. You might want to ask about:\n\n- Which alarms are most critical right now?\n- What patterns or trends do you see in the alarms?\n- What actions do you recommend based on these alarms?\n- How many active alarms require attention?\n\nOr you can select a specific alarm to get detailed analysis.`;
  }

  // Handle individual alarm analysis questions
  if (!alarm) {
    return "I don't have information about any specific alarm to answer this question. Please select an alarm first, or view the alarm summary.";
  }

  if (lowercaseQuestion.includes('how') && lowercaseQuestion.includes('fix')) {
    return `To fix this ${alarm.type} issue on ${alarm.deviceName}, I recommend following these steps:\n\n1. First, verify the alarm is still active by checking the current status\n2. Follow the troubleshooting steps in the maintenance manual for code ${alarm.code}\n3. If the issue persists after basic troubleshooting, consider contacting technical support`;
  }

  if (lowercaseQuestion.includes('why') && lowercaseQuestion.includes('happen')) {
    return `This ${alarm.type} alarm (${alarm.code}) on ${alarm.deviceName} likely occurred due to one of these common causes:\n\n- Regular wear and tear on components\n- Environmental factors like temperature extremes\n- Power supply issues\n- Scheduled maintenance being overdue\n\nThe system logs might provide more specific information about the exact trigger.`;
  }

  if (lowercaseQuestion.includes('prevent') || lowercaseQuestion.includes('avoid')) {
    return `To prevent future ${alarm.type} alarms on ${alarm.deviceName}:\n\n1. Implement a regular maintenance schedule\n2. Monitor system parameters proactively\n3. Replace aging components before they fail\n4. Ensure proper environmental conditions\n5. Keep firmware and software updated`;
  }

  if (lowercaseQuestion.includes('serious') || lowercaseQuestion.includes('critical') || lowercaseQuestion.includes('important')) {
    const severityLevel = alarm.severity === 'critical' ? 'high' : (alarm.severity === 'warning' ? 'moderate' : 'low');
    return `This ${alarm.severity} alarm has a ${severityLevel} priority level. ${alarm.severity === 'critical' ? 'It requires immediate attention to prevent potential system damage or failure.' : (alarm.severity === 'warning' ? 'It should be addressed soon to prevent potential escalation.' : 'It\'s primarily informational but should still be monitored.')}`;
  }

  if (lowercaseQuestion.includes('similar') || lowercaseQuestion.includes('other') || lowercaseQuestion.includes('related')) {
    const similarAlarms = alarms.filter(a =>
      a.id !== alarm.id &&
      (a.type === alarm.type || a.deviceId === alarm.deviceId || a.code === alarm.code)
    );

    if (similarAlarms.length === 0) {
      return `There are no other alarms similar to this ${alarm.type} alarm on ${alarm.deviceName}. This appears to be an isolated incident.`;
    }

    let response = `I found ${similarAlarms.length} related alarms:\n\n`;
    similarAlarms.slice(0, 3).forEach((a, index) => {
      response += `${index + 1}. **${a.deviceName}**: ${a.message} (${a.code})\n`;
      response += `   - ${a.severity} ${a.type} alarm, currently ${a.status}\n`;
    });

    if (similarAlarms.length > 3) {
      response += `\nAnd ${similarAlarms.length - 3} more related alarms.`;
    }

    response += `\n\nThis suggests a potential pattern that might require broader investigation.`;
    return response;
  }

  return `I can help answer specific questions about this ${alarm.type} alarm on ${alarm.deviceName}. You might want to ask:\n\n- How can I fix this issue?\n- Why did this alarm happen?\n- How serious is this alarm?\n- How can I prevent this in the future?\n- Are there similar alarms in the system?`;
}
